import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["templateId", "content", "abcTemplates", "popTemplates", "filterLink", "name", "search"]
  static values = {
    filter: { type: String, default: "abc_asc" },
    selectedTemplateId: { type: String, default: "" },
    selectedTemplateIndex: { type: Number, default: 0 },
    ascTemplates: Array,
    popTemplates: Array,
    mode: { type: String, default: "" },
    previewPathTemplate: { type: String, default: "" }
  }

  connect() {
    const unifiedPayload = this.element.dataset.templateChooserTemplatesValue
    if (unifiedPayload) {
      const templates = JSON.parse(unifiedPayload)
      this.ascTemplatesValue = templates
      this.popTemplatesValue = templates
    } else {
      this.ascTemplatesValue = JSON.parse(this.element.dataset.templateChooserAscArticleTemplatesValue || "[]")
      this.popTemplatesValue = JSON.parse(this.element.dataset.templateChooserPopArticleTemplatesValue || "[]")
    }

    if (this.ascTemplatesValue[0]) {
      this.selectedTemplateIdValue = this.ascTemplatesValue[0].id
      this.nameTarget.value = this.ascTemplatesValue[0].name
      this.updateContent()
    }

    // Add search input listener
    this.searchTarget.addEventListener('input', this.handleSearch.bind(this))
  }

  handleSearch(event) {
    const searchTerm = event.target.value.toLowerCase()
    const currentTemplates = this.filterValue === "abc_asc" ? this.ascTemplatesValue : this.popTemplatesValue
    const templateContainer = this.filterValue === "abc_asc" ? this.abcTemplatesTarget : this.popTemplatesTarget
    const templates = templateContainer.querySelectorAll('.template_chooser__template')

    templates.forEach((template, index) => {
      const templateName = currentTemplates[index].name.toLowerCase()
      const isVisible = templateName.includes(searchTerm)
      template.style.display = isVisible ? '' : 'none'
    })

    // Select first visible template
    const firstVisibleTemplate = templateContainer.querySelector('.template_chooser__template:not([style*="display: none"])')
    if (firstVisibleTemplate) {
      this.chooseTemplate({ preventDefault: () => {}, currentTarget: firstVisibleTemplate })
    }
  }

  changeFilter(event) {
    event.preventDefault()
    const newFilter = event.currentTarget.dataset.filter
    this.filterValue = newFilter
    this.selectedTemplateIndexValue = 0

    // Update filter link states
    this.filterLinkTargets.forEach(link => {
      link.classList.toggle('selected', link.dataset.filter === newFilter)
    })

    if (this.filterValue === "abc_asc") {
      this.selectedTemplateIdValue = this.ascTemplatesValue[0].id
      this.abcTemplatesTarget.classList.remove("hidden")
      this.popTemplatesTarget.classList.add("hidden")
    } else {
      this.selectedTemplateIdValue = this.popTemplatesValue[0].id
      this.abcTemplatesTarget.classList.add("hidden")
      this.popTemplatesTarget.classList.remove("hidden")
    }

    // Reset selected state on templates
    const currentTemplates = this.filterValue === "abc_asc" ?
      this.abcTemplatesTarget.querySelectorAll('.template_chooser__template') :
      this.popTemplatesTarget.querySelectorAll('.template_chooser__template')

    currentTemplates.forEach((template, index) => {
      template.classList.toggle('selected', index === 0)
    })

    this.updateContent()
  }

  chooseTemplate(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index)
    const id = event.currentTarget.dataset.templateId

    this.selectedTemplateIdValue = id
    this.selectedTemplateIndexValue = index

    // Update selected class for the current filter's templates
    const container = event.currentTarget.closest('[data-template-chooser-target]')
    const templates = container.querySelectorAll('.template_chooser__template')
    templates.forEach(t => t.classList.remove('selected'))
    event.currentTarget.classList.add('selected')

    // Set the name field to the selected template's name
    const currentTemplates = this.filterValue === "abc_asc" ? this.ascTemplatesValue : this.popTemplatesValue
    this.nameTarget.value = currentTemplates[index].name

    this.updateContent()
  }

  updateContent() {
    this.templateIdTarget.value = this.selectedTemplateIdValue
    const templates = this.filterValue === "abc_asc" ? this.ascTemplatesValue : this.popTemplatesValue
    const template = templates[this.selectedTemplateIndexValue]
    if (!template) return

    const bodyClass = this.modeValue === "process" ?
      "template_chooser__content__body template_chooser__content__body--process" :
      "template_chooser__content__body"

    if (this.modeValue === "process") {
      this.renderPreview(template.name, this.formatProcessTemplateContent(template.content), bodyClass)
      return
    }

    if (template.content) {
      this.renderPreview(template.name, template.content, bodyClass)
      return
    }

    this.renderPreview(template.name, "", bodyClass)
    this.fetchArticlePreview(template, bodyClass)
  }

  renderPreview(name, html, bodyClass) {
    this.contentTarget.innerHTML = ""

    const titleElement = document.createElement("h1")
    titleElement.className = "template_chooser__content__title"
    titleElement.textContent = name

    const bodyElement = document.createElement("div")
    bodyElement.className = bodyClass
    bodyElement.innerHTML = html || ""

    this.contentTarget.appendChild(titleElement)
    this.contentTarget.appendChild(bodyElement)
  }

  async fetchArticlePreview(template, bodyClass) {
    if (!this.previewPathTemplateValue || !template.slug) return

    const url = this.previewPathTemplateValue.replace("TEMPLATE_ID", template.slug)
    const response = await fetch(url, {
      headers: { Accept: "text/html" },
      credentials: "same-origin"
    })
    if (!response.ok) return

    const html = await response.text()
    template.content = html

    if (String(this.selectedTemplateIdValue) === String(template.id)) {
      this.renderPreview(template.name, html, bodyClass)
    }
  }

  formatProcessTemplateContent(content) {
    const temp = document.createElement("div")
    temp.innerHTML = content

    const sourceNodes = (temp.childElementCount === 1 && temp.firstElementChild.tagName === "DIV") ?
      Array.from(temp.firstElementChild.childNodes) :
      Array.from(temp.childNodes)

    const entries = []
    let textBuffer = []

    const flushTextBuffer = () => {
      if (textBuffer.length === 0) return

      const itemContainer = document.createElement("div")
      textBuffer.forEach((node) => itemContainer.appendChild(node.cloneNode(true)))

      if (itemContainer.textContent.trim().length > 0 || itemContainer.querySelector("*")) {
        entries.push({
          type: "numbered",
          html: itemContainer.innerHTML
        })
      }

      textBuffer = []
    }

    const pushListItems = (listNode) => {
      Array.from(listNode.children).forEach((li) => {
        const html = li.innerHTML.trim()
        if (!html) return

        entries.push({
          type: "bullet",
          html: html
        })
      })
    }

    sourceNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "BR") {
        flushTextBuffer()
        return
      }

      if (node.nodeType === Node.ELEMENT_NODE && (node.tagName === "UL" || node.tagName === "OL")) {
        flushTextBuffer()
        pushListItems(node)
        return
      }

      textBuffer.push(node)
    })

    flushTextBuffer()

    if (entries.length === 0) return content

    return entries.map((entry) => {
      if (entry.type === "bullet") {
        return `<div class="template_chooser__content__item template_chooser__content__item--bullet"><span class="template_chooser__content__item__bullet">•</span><span class="template_chooser__content__item__text">${entry.html}</span></div>`
      }

      return `<div class="template_chooser__content__item template_chooser__content__item--numbered">${entry.html}</div>`
    }).join("")
  }
}
